import React, { useEffect, useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { cn } from "../../../lib/cn";
import Button from "../Button";

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

const Modal = ({
  children,
  isOpen = false,
  onClose,
  title,
  size = "md",
  className = "",
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  ...props
}) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const scrollLockCountRef = useRef(0);

  useIsomorphicLayoutEffect(() => {
    if (isOpen) {
      scrollLockCountRef.current += 1;
      
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const hasScrollbar = scrollbarWidth > 0;
      
      const scrollY = window.scrollY;
      const style = document.body.style;
      const originalOverflow = style.overflow;
      const originalPaddingRight = style.paddingRight;
      const originalPosition = style.position;
      const originalTop = style.top;
      const originalWidth = style.width;
      
      style.position = "fixed";
      style.top = `-${scrollY}px`;
      style.left = "0";
      style.right = "0";
      style.overflow = "hidden";
      
      if (hasScrollbar) {
        style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        scrollLockCountRef.current -= 1;
        if (scrollLockCountRef.current === 0) {
          style.position = originalPosition || "";
          style.top = originalTop || "";
          style.left = "";
          style.right = "";
          style.overflow = originalOverflow || "";
          style.paddingRight = originalPaddingRight || "";
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && closeOnEscape && isOpen) {
        onClose();
      }
    };

    if (isOpen && closeOnEscape) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, closeOnEscape]);

  useEffect(() => {
    let animationFrameId = null;
    
    if (isOpen && modalRef.current) {
      previousFocusRef.current = document.activeElement;
      
      animationFrameId = requestAnimationFrame(() => {
        if (modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          );
          if (focusableElements.length > 0) {
            focusableElements[0].focus({ preventScroll: true });
          }
        }
      });
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      if (previousFocusRef.current && document.contains(previousFocusRef.current)) {
        previousFocusRef.current.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  const sizes = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[var(--z-index-modal)] overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-60 transition-opacity backdrop-blur-sm"
        onClick={handleOverlayClick}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={cn(
          "relative bg-[var(--color-bg-secondary)] rounded-2xl shadow-2xl mx-auto my-8",
          "flex flex-col max-h-[90vh] border border-[var(--color-border)]",
          sizes[size],
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
        {...props}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
            {title && (
              <h3
                id="modal-title"
                className="text-xl font-black text-[var(--color-text-primary)] tracking-tight"
              >
                {title}
              </h3>
            )}
            {showCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label="Закрыть"
                className="hover:bg-[var(--color-bg-tertiary)]"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 px-6 py-6 overflow-y-auto text-[var(--color-text-primary)]">
          {children}
        </div>
      </div>
    </div>
  );
};

Modal.propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  className: PropTypes.string,
  showCloseButton: PropTypes.bool,
  closeOnOverlayClick: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
};

export default Modal;
